/**
 * A module and its board, in one place.
 *
 * The module's screen, the leaderboard's screen and the end of a run all need the
 * same four things — the record, its playlist, the plays of those capsules, and
 * the board built from them — and all three are looking at the same class, so they
 * must not each decide separately what "a play of this module" means. This is that
 * decision, once.
 *
 * Two things it deliberately does *not* ask for:
 *
 *  - **The capsules' own records.** A capsule's maximum points live in its slides,
 *    and fetching six capsule records to print a percentage on a row would trade a
 *    board for a spinner. Points and time are what the board ranks on and both are
 *    in the plays; the percentage is `null` (unknown, not zero) and the card simply
 *    does not print one.
 *  - **Every play in the school.** The plays are asked for by the module's own
 *    capsule uris, in one request, which is exactly what the AppView is being asked
 *    to answer (see `#/state/queries/capsule-plays`).
 *
 * The preview is the one caller that *does* hand the board a maximum: an invented
 * class on a module of real capsules needs numbers to score against, so a preview
 * gives every capsule a nominal ten (see `#/lib/moduleFixtures`).
 */
import {useMemo} from 'react'

import {
  buildModuleLeaderboard,
  type ModuleLeaderboard,
} from '#/lib/moduleLeaderboard'
import {modulePreviewPlays, type ModulePreviewState} from '#/lib/moduleFixtures'
import {type ModuleItem} from '#/lib/modulePlaylist'
import {type ModuleRecordView} from '#/lib/moduleRecord'
import {useModulePlaysQuery} from '#/state/queries/capsule-plays'
import {useModuleByUriQuery} from '#/state/queries/module'

/** What a preview gives a capsule to be worth, so its rows have numbers. */
const PREVIEW_CAPSULE_POINTS = 10

export function useModuleBoard({
  moduleUri,
  preview,
}: {
  moduleUri: string | undefined
  /** Dev-only: draw invented plays instead of the class's. */
  preview: ModulePreviewState | null
}): {
  record: ModuleRecordView | undefined
  items: ModuleItem[]
  board: ModuleLeaderboard
  /** The record is still on its way. */
  loading: boolean
  /** The record could not be read at all. */
  failed: boolean
  /** Whether the board is drawing invented plays. */
  isPreview: boolean
} {
  const moduleQuery = useModuleByUriQuery(moduleUri)
  const items = useMemo(() => moduleQuery.data?.items ?? [], [moduleQuery.data])

  /*
   * Nothing is asked of the AppView while a preview is on.
   *
   * The invented plays are the answer; a request beside them would be one the
   * screen throws away, and a preview that still hit the network would be a
   * preview a dev build could not use offline.
   */
  const uris = useMemo(() => items.map(item => item.uri), [items])
  const playsQuery = useModulePlaysQuery(preview ? [] : uris)

  const capsules = useMemo(
    () =>
      uris.map(uri => ({
        uri,
        ...(preview ? {maxPoints: PREVIEW_CAPSULE_POINTS} : null),
      })),
    [preview, uris],
  )

  const previewPlays = useMemo(
    () => (preview ? modulePreviewPlays(capsules, preview) : undefined),
    [capsules, preview],
  )

  const board = useMemo(
    () =>
      buildModuleLeaderboard({
        capsules,
        plays: previewPlays ?? playsQuery.data,
      }),
    [capsules, playsQuery.data, previewPlays],
  )

  return {
    record: moduleQuery.data,
    items,
    board,
    /*
     * A preview is never *loading* even though nothing was asked for: its plays
     * are already in hand, and the disabled query underneath it reports pending
     * for ever — which would put a spinner over a board that is fully drawn.
     */
    loading: moduleQuery.isPending || (!previewPlays && playsQuery.isPending),
    failed: moduleQuery.isError,
    isPreview: !!preview,
  }
}
